import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useBooks } from '../context/BookContext';
import { User, ShieldCheck, Zap, LogOut, Moon, Sun, Palette, Download, Trash2, AlertTriangle, X, UploadCloud, Share2, Edit2, Check, BookOpen, Terminal, Rocket, Sparkles, Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import ImportWizard from '../components/ImportWizard';
import ShareCard from '../components/ShareCard';

// Founder reward model isn't finalized yet - keep granting it (so we can tag
// early adopters now) but hide the badge everywhere until launch is decided.
const FOUNDER_BADGE_VISIBLE = false;

const Profile = () => {
  const { user, profile, updateProfile, signOut, switchEnvironment } = useAuth();
  const { theme, setTheme, toggleTheme, accent, setAccent, isSeasonal, setIsSeasonal, musicProvider, setMusicProvider } = useTheme();
  const { clearLibrary } = useBooks();
  const [appVersion, setAppVersion] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem('leaf_env_mode') || 'stable';
    }
    return 'stable';
  });
  const [isSaving, setIsSaving] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState(profile?.notification_prefs || { personal: true, group: true, community: true });

  const companions = [
    { emoji: '🦊', name: 'Cozy Fox' },
    { emoji: '🦉', name: 'Wise Owl' },
    { emoji: '🦔', name: 'Hedgehog' },
    { emoji: '🐉', name: 'Book Dragon' },
    { emoji: '🐱', name: 'Kitten' },
    { emoji: '🐼', name: 'Panda' },
    { emoji: '🐸', name: 'Tea Frog' },
    { emoji: '☕', name: 'Hot Cocoa' },
  ];
  
  // Data State
  const [books, setBooks] = useState([]);
  const [logs, setLogs] = useState([]);
  
  // Username State
  const [username, setUsername] = useState('');
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  
  // UI State
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');

  useEffect(() => {
    // Read the actual active runtime environment preference from LocalStorage to prevent visual mismatch
    if (typeof window !== 'undefined' && window.localStorage) {
      const activeMode = window.localStorage.getItem('leaf_env_mode') || 'stable';
      setAppVersion(activeMode);
    }
    
    if (user) {
      fetchUserData();
    }
    if (profile) {
      setUsername(profile.username || '');
      setNotifPrefs(profile.notification_prefs || { personal: true, group: true, community: true });
    }
  }, [user, profile]);

  const fetchUserData = async () => {
    const { data: b } = await supabase.from('books').select('*').eq('user_id', user.id);
    const { data: l } = await supabase.from('reading_logs').select('*').eq('user_id', user.id);
    if (b) setBooks(b);
    if (l) setLogs(l);
  };

  const saveUsername = async () => {
    setIsSaving(true);
    const { error } = await updateProfile({ username });
    if (error) {
      alert("Failed to update username. It might be taken.");
    } else {
      setIsEditingUsername(false);
    }
    setIsSaving(false);
  };

  const handleVersionChange = async (version) => {
    setAppVersion(version);
    setIsSaving(true);
    
    // Save metadata preference
    await supabase.auth.updateUser({
      data: { app_version: version }
    });

    // Dynamically flip environments and reload the state
    switchEnvironment(version);
    setIsSaving(false);
  };

  const handleNotifPrefToggle = async (category) => {
    const updated = { ...notifPrefs, [category]: !notifPrefs[category] };
    setNotifPrefs(updated);
    setIsSaving(true);
    await updateProfile({ notification_prefs: updated });
    setIsSaving(false);
  };

  const handleExport = async () => {
    if (!user) return;
    setIsExporting(true);
    try {
      const exportData = {
        user: { email: user.email, id: user.id, metadata: user.user_metadata, profile },
        books: books || [],
        logs: logs || [],
        exportDate: new Date().toISOString()
      };

      // Create Blob and Download
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leaf-and-letter-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteEmail !== user.email) {
      alert("Email does not match. Please type your full email address to confirm.");
      return;
    }

    if (!window.confirm("Final Warning: This action cannot be undone. Are you absolutely sure?")) {
      return;
    }

    setIsDeleting(true);
    try {
      await supabase.from('books').delete().eq('user_id', user.id);
      await supabase.from('reading_logs').delete().eq('user_id', user.id);
      await supabase.from('profiles').delete().eq('id', user.id);
      
      alert("Your data has been deleted. You will now be signed out.");
      await signOut();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete account data: " + error.message);
      setIsDeleting(false);
    }
  };

  const accents = [
    { id: 'forest', name: 'Forest', color: '#4caf50' },
    { id: 'ocean', name: 'Ocean', color: '#2196f3' },
    { id: 'sunset', name: 'Sunset', color: '#ff9800' },
    { id: 'lavender', name: 'Lavender', color: '#9c27b0' },
    { id: 'rose', name: 'Rose', color: '#e91e63' },
    { id: 'slate', name: 'Slate', color: '#607d8b' },
    { id: 'amber', name: 'Amber', color: '#ffc107' },
  ];

  if (!user) return <p style={{ textAlign: 'center', marginTop: '2rem', color: 'var(--text-main)' }}>Please sign in to view your profile.</p>;

  return (
    <div style={{ maxWidth: '500px', margin: '0 auto', position: 'relative' }}>
      <div style={headerStyle}>
        <div style={avatarCircle}>
          {profile?.avatar_url ? (
            <span style={{ fontSize: '2.5rem' }}>{profile.avatar_url}</span>
          ) : (
            <User size={40} color="var(--text-soft)" />
          )}
        </div>
        
        {/* Username Editor */}
        <div style={{ margin: '1rem 0 0.5rem 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          {isEditingUsername ? (
            <div style={{ display: 'flex', gap: '4px' }}>
              <input 
                value={username} 
                onChange={(e) => setUsername(e.target.value)} 
                style={usernameInput}
                placeholder="Username"
              />
              <button onClick={saveUsername} style={saveUserBtn}><Check size={18} /> Save</button>
              <button onClick={() => setIsEditingUsername(false)} style={cancelUserBtn}>Cancel</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <h2 style={{ margin: 0, color: 'var(--text-main)' }}>{username || 'Anonymous'}</h2>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {FOUNDER_BADGE_VISIBLE && profile?.is_founder && <span style={{ fontSize: '0.7rem', backgroundColor: '#ffd700', color: '#5a3e00', fontWeight: 'bold', padding: '2px 8px', borderRadius: '10px' }}>🌿 Founder</span>}
                {profile?.role === 'employee' && <span style={{ fontSize: '0.7rem', backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', padding: '2px 8px', borderRadius: '10px' }}>👔 Employee</span>}
                {profile?.role === 'partner' && <span style={{ fontSize: '0.7rem', backgroundColor: '#f3e5f5', color: '#6a1b9a', fontWeight: 'bold', padding: '2px 8px', borderRadius: '10px' }}>🤝 Partner</span>}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setIsEditingUsername(true)} style={editUserBtn}>
                  <Edit2 size={14} /> Change Username
                </button>
                <button 
                  onClick={() => updateProfile({ share_stats: !profile?.share_stats })}
                  style={{
                    ...privacyToggleBtn,
                    backgroundColor: profile?.share_stats ? 'var(--accent)' : 'var(--text-soft)',
                  }}
                >
                  {profile?.share_stats ? 'Public Profile' : 'Private Profile'}
                </button>
              </div>
            </div>
          )}
        </div>

        <p style={{ color: 'var(--text-soft)', fontSize: '0.8rem', marginBottom: '1rem' }}>{user.email}</p>
        
        <button onClick={() => setShowShareCard(true)} style={shareBtn}>
          <Share2 size={16} /> Share Card
        </button>
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeader}>
          <Palette size={20} color="var(--accent)" />
          <h3 style={{ margin: 0, color: 'var(--text-main)' }}>Appearance</h3>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', marginTop: '1rem' }}>
          <div>
            <div style={{ fontWeight: 'bold', color: 'var(--text-main)' }}>Theme Mode</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>Choose your aesthetic.</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', width: '100%' }}>
            <button onClick={() => setTheme('light')} style={theme === 'light' ? activeThemeTab : themeTab}>
              <Sun size={16} /> Light
            </button>
            <button onClick={() => setTheme('dark')} style={theme === 'dark' ? activeThemeTab : themeTab}>
              <Moon size={16} /> Dark
            </button>
            <button onClick={() => setTheme('lofi')} style={theme === 'lofi' ? activeThemeTab : themeTab}>
              <Sparkles size={16} /> Lo-Fi
            </button>
            <button onClick={() => setTheme('sanctuary')} style={theme === 'sanctuary' ? activeThemeTab : themeTab}>
              <BookOpen size={16} /> Sanct.
            </button>
            <button onClick={() => setTheme('terminal')} style={theme === 'terminal' ? activeThemeTab : themeTab}>
              <Terminal size={16} /> Term.
            </button>
            <button onClick={() => setTheme('spaceship')} style={theme === 'spaceship' ? activeThemeTab : themeTab}>
              <Rocket size={16} /> Space
            </button>
            <button onClick={() => setTheme('electric')} style={theme === 'electric' ? activeThemeTab : themeTab}>
              <Zap size={16} /> Elec.
            </button>
          </div>
        </div>

        <div style={{ fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '0.8rem' }}>Color Accent</div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', backgroundColor: 'var(--bg-app)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'var(--text-main)' }}>Seasonal Tinting</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)' }}>Auto-adjust colors based on the time of year.</div>
          </div>
          <button 
            onClick={() => setIsSeasonal(!isSeasonal)}
            style={{
              padding: '6px 12px',
              borderRadius: '20px',
              border: 'none',
              backgroundColor: isSeasonal ? 'var(--accent)' : 'var(--text-soft)',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            {isSeasonal ? 'On' : 'Off'}
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', backgroundColor: 'var(--bg-app)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'var(--text-main)' }}>Music Provider</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)' }}>Choose your Focus player.</div>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button 
              onClick={() => setMusicProvider('youtube')}
              style={{
                ...themeTab,
                backgroundColor: musicProvider === 'youtube' ? 'var(--accent)' : 'transparent',
                color: musicProvider === 'youtube' ? 'white' : 'var(--text-soft)',
                borderColor: musicProvider === 'youtube' ? 'var(--accent)' : 'var(--border)'
              }}
            >
              YouTube
            </button>
            <button 
              onClick={() => setMusicProvider('soundcloud')}
              style={{
                ...themeTab,
                backgroundColor: musicProvider === 'soundcloud' ? 'var(--accent)' : 'transparent',
                color: musicProvider === 'soundcloud' ? 'white' : 'var(--text-soft)',
                borderColor: musicProvider === 'soundcloud' ? 'var(--accent)' : 'var(--border)'
              }}
            >
              SC
            </button>
          </div>
        </div>

        {!isSeasonal && (
          <div style={accentGrid}>
            {accents.map(a => (
              <button 
                key={a.id}
                onClick={() => setAccent(a.id)}
                style={{
                  ...accentBtn,
                  borderColor: accent === a.id ? a.color : 'var(--border)',
                  backgroundColor: accent === a.id ? a.color : 'transparent',
                  color: accent === a.id ? 'white' : 'var(--text-main)'
                }}
              >
                <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: a.color, border: '1px solid white' }} />
                {a.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeader}>
          <Sparkles size={20} color="var(--accent)" />
          <h3 style={{ margin: 0, color: 'var(--text-main)' }}>Sanctuary Companion</h3>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-soft)', marginTop: '0.5rem', marginBottom: '1rem' }}>
          Select a cozy reading buddy to accompany your presence inside the real-time Communal Sanctuary room!
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
          {companions.map(c => {
            const isSelected = profile?.avatar_url === c.emoji;
            return (
              <button
                key={c.emoji}
                onClick={async () => {
                  await updateProfile({ avatar_url: c.emoji });
                  window.dispatchEvent(new CustomEvent('show-toast', { detail: { message: `Companion changed to ${c.name}! 🌟`, type: 'success' } }));
                }}
                style={isSelected ? activeCompanionCard : companionCard}
              >
                <span style={{ fontSize: '2rem' }}>{c.emoji}</span>
                <span style={{ fontSize: '0.65rem', fontWeight: 'bold', color: isSelected ? 'var(--accent)' : 'var(--text-soft)', marginTop: '4px' }}>{c.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeader}>
          <Bell size={20} color="var(--accent)" />
          <h3 style={{ margin: 0, color: 'var(--text-main)' }}>Notifications</h3>
        </div>
        <p style={descriptionStyle}>Choose which notification categories you want to see in the bell panel.</p>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", backgroundColor: "var(--bg-app)", padding: "12px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--text-main)" }}>Personal</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-soft)" }}>Notifications about your own activity.</div>
          </div>
          <button
            onClick={() => handleNotifPrefToggle('personal')}
            style={{ padding: "6px 12px", borderRadius: "20px", border: "none", backgroundColor: notifPrefs.personal ? "var(--accent)" : "var(--text-soft)", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer" }}
          >
            {notifPrefs.personal ? "On" : "Off"}
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", backgroundColor: "var(--bg-app)", padding: "12px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--text-main)" }}>Group / Circle</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-soft)" }}>Notifications from your reading groups.</div>
          </div>
          <button
            onClick={() => handleNotifPrefToggle('group')}
            style={{ padding: "6px 12px", borderRadius: "20px", border: "none", backgroundColor: notifPrefs.group ? "var(--accent)" : "var(--text-soft)", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer" }}
          >
            {notifPrefs.group ? "On" : "Off"}
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", backgroundColor: "var(--bg-app)", padding: "12px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--text-main)" }}>Community</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-soft)" }}>Notifications from the wider community.</div>
          </div>
          <button
            onClick={() => handleNotifPrefToggle('community')}
            style={{ padding: "6px 12px", borderRadius: "20px", border: "none", backgroundColor: notifPrefs.community ? "var(--accent)" : "var(--text-soft)", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer" }}
          >
            {notifPrefs.community ? "On" : "Off"}
          </button>
        </div>
        {isSaving && <p style={{ fontSize: '0.8rem', color: 'var(--accent)', marginTop: '8px' }}>Saving preference...</p>}
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeader}>
          <Zap size={20} color="#2196f3" />
          <h3 style={{ margin: 0, color: 'var(--text-main)' }}>App Experience</h3>
        </div>
        <p style={descriptionStyle}>Choose which version of Leaf & Letter you want to use.</p>
        
        <div style={versionToggleContainer}>
          <button 
            style={appVersion === 'stable' ? activeVersionStyle : versionButtonStyle}
            onClick={() => handleVersionChange('stable')}
          >
            <ShieldCheck size={18} />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: 'bold' }}>Stable</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>Recommended for most users.</div>
            </div>
          </button>

          <button 
            style={appVersion === 'test' ? activeTestStyle : versionButtonStyle}
            onClick={() => handleVersionChange('test')}
          >
            <Zap size={18} />
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: 'bold' }}>Test (Beta)</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>Early access to new features.</div>
            </div>
          </button>
        </div>
        {isSaving && <p style={{ fontSize: '0.8rem', color: 'var(--accent)', marginTop: '8px' }}>Saving preference...</p>}
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeader}>
          <ShieldCheck size={20} color="#ff9800" />
          <h3 style={{ margin: 0, color: 'var(--text-main)' }}>Data & Privacy</h3>
        </div>
        <p style={descriptionStyle}>Manage your personal data and privacy settings.</p>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", backgroundColor: "var(--bg-app)", padding: "12px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--text-main)" }}>Nook Peeking</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-soft)" }}>Let others browse your bookshelf.</div>
          </div>
          <button
            onClick={() => updateProfile({ nook_public: !profile?.nook_public })}
            style={{ padding: "6px 12px", borderRadius: "20px", border: "none", backgroundColor: profile?.nook_public ? "var(--accent)" : "var(--text-soft)", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer" }}
          >
            {profile?.nook_public ? "On" : "Off"}
          </button>
        </div>

        <button onClick={() => setShowImportWizard(true)} style={importButtonStyle}>
          <UploadCloud size={18} /> Import from Goodreads (CSV)
        </button>

        <button onClick={handleExport} disabled={isExporting} style={exportButtonStyle}>
          <Download size={18} /> {isExporting ? "Exporting..." : "Export My Data (JSON)"}
        </button>

        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px dashed var(--border)' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#ff5252', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <AlertTriangle size={16} /> DANGER ZONE
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button onClick={clearLibrary} style={resetButtonStyle}>
              Reset Sanctuary
            </button>
            <button onClick={() => setShowDeleteModal(true)} style={deleteButtonStyle}>
              Delete Account
            </button>
          </div>
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionHeader}>
          <Bell size={20} color="var(--accent)" />
          <h3 style={{ margin: 0, color: 'var(--text-main)' }}>Notifications</h3>
        </div>
        <p style={descriptionStyle}>Choose which notification categories you want to see in the bell panel.</p>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", backgroundColor: "var(--bg-app)", padding: "12px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--text-main)" }}>Personal</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-soft)" }}>Notifications about your own activity.</div>
          </div>
          <button
            onClick={() => handleNotifPrefToggle('personal')}
            style={{ padding: "6px 12px", borderRadius: "20px", border: "none", backgroundColor: notifPrefs.personal ? "var(--accent)" : "var(--text-soft)", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer" }}
          >
            {notifPrefs.personal ? "On" : "Off"}
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", backgroundColor: "var(--bg-app)", padding: "12px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--text-main)" }}>Group / Circle</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-soft)" }}>Notifications from your reading groups.</div>
          </div>
          <button
            onClick={() => handleNotifPrefToggle('group')}
            style={{ padding: "6px 12px", borderRadius: "20px", border: "none", backgroundColor: notifPrefs.group ? "var(--accent)" : "var(--text-soft)", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer" }}
          >
            {notifPrefs.group ? "On" : "Off"}
          </button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", backgroundColor: "var(--bg-app)", padding: "12px", borderRadius: "12px", border: "1px solid var(--border)" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", fontSize: "0.9rem", color: "var(--text-main)" }}>Community</div>
            <div style={{ fontSize: "0.75rem", color: "var(--text-soft)" }}>Notifications from the wider community.</div>
          </div>
          <button
            onClick={() => handleNotifPrefToggle('community')}
            style={{ padding: "6px 12px", borderRadius: "20px", border: "none", backgroundColor: notifPrefs.community ? "var(--accent)" : "var(--text-soft)", color: "white", fontSize: "0.75rem", fontWeight: "bold", cursor: "pointer" }}
          >
            {notifPrefs.community ? "On" : "Off"}
          </button>
        </div>
        {isSaving && <p style={{ fontSize: '0.8rem', color: 'var(--accent)', marginTop: '8px' }}>Saving preference...</p>}
      </div>

      <button onClick={signOut} style={logoutButtonStyle}>
        <LogOut size={18} /> Sign Out
      </button>

      {showImportWizard && <ImportWizard onClose={() => setShowImportWizard(false)} onImportComplete={() => setShowImportWizard(false)} />}
      {showShareCard && <ShareCard user={user} books={books} onClose={() => setShowShareCard(false)} />}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <button onClick={() => setShowDeleteModal(false)} style={closeBtn}><X size={20} /></button>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <div style={warningIconCircle}>
                <Trash2 size={32} color="#ff5252" />
              </div>
              <h3 style={{ color: 'var(--text-main)', marginTop: '1rem' }}>Delete Account?</h3>
              <p style={{ color: 'var(--text-soft)', fontSize: '0.9rem' }}>
                This will <b>permanently delete</b> all your books, reading logs, and preferences. 
                This action cannot be undone.
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '5px' }}>
                Type your email to confirm: <span style={{color: 'var(--accent)'}}>{user.email}</span>
              </label>
              <input 
                type="text" 
                value={deleteEmail}
                onChange={(e) => setDeleteEmail(e.target.value)}
                placeholder={user.email}
                style={inputStyle}
              />
            </div>

            <button 
              onClick={handleDeleteAccount} 
              disabled={deleteEmail !== user.email || isDeleting}
              style={{
                ...confirmDeleteBtn,
                opacity: deleteEmail === user.email ? 1 : 0.5,
                cursor: deleteEmail === user.email ? 'pointer' : 'not-allowed'
              }}
            >
              {isDeleting ? "Deleting..." : "Permanently Delete Account"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const headerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '1rem 0 2rem 0',
  borderBottom: '1px solid var(--border)',
  marginBottom: '2rem'
};

const shareBtn = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  borderRadius: '20px',
  border: '1px solid var(--accent)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--accent)',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: 'bold'
};

const editUserBtn = {
  background: 'none',
  border: '1px solid var(--border)',
  borderRadius: '12px',
  padding: '6px 12px',
  fontSize: '0.75rem',
  fontWeight: 'bold',
  color: 'var(--text-soft)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
};

const saveUserBtn = {
  ...editUserBtn,
  backgroundColor: 'var(--accent)',
  color: 'white',
  borderColor: 'var(--accent)'
};

const cancelUserBtn = {
  ...editUserBtn,
  border: 'none'
};

const privacyToggleBtn = {
  ...editUserBtn,
  color: 'white',
  border: 'none'
};

const iconBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px'
};

const usernameInput = {
  padding: '8px',
  borderRadius: '8px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-main)',
  fontSize: '1rem',
  width: '150px'
};

const avatarCircle = {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: 'var(--bg-app)',
  border: '1px solid var(--border)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const sectionStyle = {
  backgroundColor: 'var(--bg-card)',
  padding: '1.5rem',
  borderRadius: '16px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  border: '1px solid var(--border)',
  marginBottom: '1.5rem'
};

const sectionHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '0.5rem'
};

const descriptionStyle = {
  fontSize: '0.9rem',
  color: 'var(--text-soft)',
  marginBottom: '1.5rem',
  lineHeight: '1.4'
};

const themeToggleBtn = {
  padding: '10px',
  borderRadius: '12px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-main)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const themeTab = {
  ...themeToggleBtn,
  padding: '8px 12px',
  backgroundColor: 'transparent',
  color: 'var(--text-soft)'
};

const activeThemeTab = {
  ...themeTab,
  backgroundColor: 'var(--accent)',
  color: 'white',
  borderColor: 'var(--accent)'
};

const accentGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '10px'
};

const accentBtn = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px',
  borderRadius: '10px',
  border: '2px solid transparent',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  transition: 'all 0.2s'
};

const versionToggleContainer = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
};

const versionButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  borderRadius: '12px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  cursor: 'pointer',
  transition: 'all 0.2s',
  color: 'var(--text-soft)'
};

const activeVersionStyle = {
  ...versionButtonStyle,
  backgroundColor: 'var(--accent-soft)',
  borderColor: 'var(--accent)',
  color: 'var(--accent)'
};

const activeTestStyle = {
  ...versionButtonStyle,
  backgroundColor: '#e3f2fd',
  borderColor: '#2196f3',
  color: '#1565c0'
};

const logoutButtonStyle = {
  width: '100%',
  padding: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  backgroundColor: 'transparent',
  border: '1px solid var(--text-soft)',
  color: 'var(--text-soft)',
  borderRadius: '12px',
  cursor: 'pointer',
  fontWeight: 'bold',
  marginTop: '1rem',
  marginBottom: '2rem'
};

const exportButtonStyle = {
  width: '100%',
  padding: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  backgroundColor: 'var(--bg-app)',
  border: '1px solid var(--border)',
  color: 'var(--text-main)',
  borderRadius: '12px',
  cursor: 'pointer',
  fontWeight: 'bold'
};

const importButtonStyle = {
  ...exportButtonStyle,
  backgroundColor: 'var(--accent-soft)', 
  borderColor: 'var(--accent)',
  color: 'var(--accent)',
  marginBottom: '1rem'
};

const deleteButtonStyle = {
  width: '100%',
  padding: '10px',
  backgroundColor: '#ffebee',
  border: '1px solid #ffcdd2',
  color: '#d32f2f',
  borderRadius: '10px',
  cursor: 'pointer',
  fontWeight: 'bold',
  fontSize: '0.9rem'
};

const resetButtonStyle = {
  ...deleteButtonStyle,
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-main)',
  borderColor: 'var(--border)'
};

// Modal Styles
const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const modalContent = {
  backgroundColor: 'var(--bg-card)',
  padding: '2rem',
  borderRadius: '20px',
  maxWidth: '400px',
  width: '100%',
  position: 'relative',
  boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
};

const closeBtn = {
  position: 'absolute',
  top: '15px',
  right: '15px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-soft)'
};

const warningIconCircle = {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  backgroundColor: '#ffebee',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  borderRadius: '8px',
  border: '1px solid var(--border)',
  backgroundColor: 'var(--bg-app)',
  color: 'var(--text-main)',
  fontSize: '1rem',
  marginTop: '5px'
};

const confirmDeleteBtn = {
  width: '100%',
  padding: '14px',
  backgroundColor: '#ff5252',
  color: 'white',
  border: 'none',
  borderRadius: '12px',
  fontWeight: 'bold',
  marginTop: '1rem'
};

const companionCard = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '10px',
  backgroundColor: 'var(--bg-app)',
  border: '1px solid var(--border)',
  borderRadius: '16px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  outline: 'none'
};

const activeCompanionCard = {
  ...companionCard,
  backgroundColor: 'var(--accent-soft)',
  borderColor: 'var(--accent)',
  boxShadow: '0 0 8px var(--accent-soft)'
};

export default Profile;
